import { useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { TabProp } from '~/components/Tabs'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureSitePage } from './PureSitePage'
import { AdmSitePageContext, useAdmSitePageContext } from '~/modules/pbs/sites/site/useSitePageContext'

type TabLabel = 'Configuration' | 'Activation' | 'Stacks' | 'Mapping' | 'GAM & wrappers' | 'Modules' | 'Connect to Pubstack' | 'Extra-scripts' | 'History'

const TabUrls: { [key in TabLabel]: string } = {
  Configuration: 'configuration',
  Activation: 'activation',
  Stacks: 'stacks',
  Mapping: 'mapping',
  'GAM & wrappers': 'gamwrappers',
  Modules: 'modulesBySite',
  'Connect to Pubstack': 'connect-to-pubstack',
  'Extra-scripts': 'extra-scripts',
  History: 'history',
}

export const SitePage = () => {
  const { siteId } = useParams()
  const { product, adUnits, bidderRules, isLoading, scopeId, stacks, site: currentSite, contexts, siteConfig, spaConfig } = useAdmSitePageContext(siteId)
  const user = useAuthenticatedUser()
  const breadcrumbs = useBreadcrumbs(currentSite)
  const navigate = useNavigate()
  const location = useLocation()

  const tabs: TabProp[] = useMemo(() => {
    const tabs: TabProp[] = [
      // Activation first, because it is where the page opens: the route with no tab in it redirects here,
      // and a first tab that is not the landing one reads as a bug the moment anyone compares the two.
      {
        label: 'Activation',
        active: /activation\/?$/.test(location?.pathname ?? ''),
        trackingLabel: 'adm-sites-activation-tab-apply',
      },
      // What the media *is* — its name and its Prebid variable — before anything about what it runs.
      {
        label: 'Configuration',
        active: /configuration\/?$/.test(location?.pathname ?? ''),
        trackingLabel: 'adm-sites-configuration-tab-apply',
      },
      {
        label: 'Stacks',
        active: /stacks\/?$/.test(location?.pathname ?? ''),
        disabled: stacks.length === 0,
        trackingLabel: 'adm-sites-stacks-tab-apply',
      },
      {
        label: 'Mapping',
        active: /mapping\/?$/.test(location?.pathname ?? ''),
        disabled: !currentSite?.adManagementEnabled,
        trackingLabel: 'adm-sites-mapping-tab-apply',
      },
      {
        label: 'GAM & wrappers',
        active: /gamwrappers\/?$/.test(location?.pathname ?? ''),
        disabled: !currentSite?.adManagementEnabled,
        trackingLabel: 'adm-sites-gam-wrappers-tab-apply',
      },
      {
        label: 'Modules',
        active: /modulesBySite\/?$/.test(location?.pathname ?? ''),
        trackingLabel: 'adm-sites-modules-tab-apply',
      },
      {
        label: 'History',
        active: /history\/?$/.test(location?.pathname ?? ''),
        trackingLabel: 'adm-sites-history-tab-apply',
      },
      {
        label: 'Connect to Pubstack',
        active: /connect-to-pubstack\/?$/.test(location?.pathname ?? ''),
        disabled: !currentSite.adManagementEnabled,
        trackingLabel: 'adm-sites-connect-tab-apply',
      },
      {
        label: 'Extra-scripts',
        active: /extra-scripts\/?$/.test(location?.pathname ?? ''),
        trackingLabel: 'adm-sites-extra-scripts-tab-apply',
      },
    ]
    return tabs.filter((t) => {
      return !t.feature || user.hasFeature(t.feature)
    })
  }, [location, stacks, currentSite, user])

  const onTabChange = (tab: TabProp) => {
    navigate(TabUrls[tab.label as TabLabel])
  }

  return (
    <PureSitePage breadcrumbs={breadcrumbs} tabs={tabs} onTabChange={onTabChange}>
      <Outlet
        context={
          {
            product,
            site: currentSite,
            stacks,
            contexts,
            adUnits,
            isLoading,
            bidderRules,
            scopeId,
            siteConfig,
            spaConfig,
          } satisfies AdmSitePageContext
        }
      />
    </PureSitePage>
  )
}
